// src/pages/DriverTrackingReports.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import AdminSidebar from '../components/AdminSidebar';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import '../pages/DriverTrackingReports.css';

import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addArabicFont } from '../assets/fonts/pdfArabicSupport';
import { useLanguage } from '../contexts/LanguageContext';
import LoadingSpinner from '../components/LoadingSpinner';

const DriverTrackingReports = () => {
  const [reportType, setReportType]     = useState('activity');
  const [dateRange, setDateRange]       = useState('week');
  const [loading, setLoading]           = useState(true);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [drivers, setDrivers]           = useState([]);
  const [driverActivities, setDriverActivities] = useState([]);
  const [driverMovements, setDriverMovements]   = useState([]);
  const [selectedDriver, setSelectedDriver]     = useState('all');
  const [activityChartData, setActivityChartData] = useState([]);
  const [availabilityData, setAvailabilityData]   = useState([]);
  const [speedData, setSpeedData]                 = useState([]);
  const [stats, setStats] = useState({
    totalDrivers: 0,
    activeDrivers: 0,
    averageActiveTime: 0,
    averageSpeed: 0,
    totalAvailableTime: 0,
  });
  const [pageSize, setPageSize]       = useState(20);
  const [hasMore, setHasMore]         = useState(true);
  const [lastVisible, setLastVisible] = useState(null);
  const [allDataFetched, setAllDataFetched] = useState(false);
  const [error, setError]             = useState('');

  const navigate = useNavigate();
  const { t, language } = useLanguage();

  const formatDate = useCallback((date) => {
    const options = { day: 'numeric', month: 'short' };
    return date.toLocaleDateString('ar-EG', options);
  }, []);

  const formatTime = useCallback((date) => {
    return date.toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' });
  }, []);

  // دالة لتجهيز بيانات مخطط النشاط
  const prepareActivityChartData = useCallback((activitiesData) => {
    const dataMap = {};
    const today = new Date();

    // إنشاء نقاط البيانات حسب النطاق الزمني
    if (dateRange === 'day') {
      // نشاط خلال اليوم (بالساعات)
      for (let hour = 0; hour < 24; hour++) {
        const timeLabel = `${hour}:00`;
        dataMap[timeLabel] = { time: timeLabel, appOpen: 0, available: 0, orders: 0 };
      }
      
      activitiesData.forEach(activity => {
        const hour = activity.date.getHours();
        const timeLabel = `${hour}:00`;
        
        if (dataMap[timeLabel]) {
          if (activity.type === 'app_open') dataMap[timeLabel].appOpen += 1;
          if (activity.type === 'available_toggle' && activity.value === true) dataMap[timeLabel].available += 1;
          if (activity.type === 'order_received') dataMap[timeLabel].orders += 1;
        }
      });
    } else {
      // نشاط خلال الأسبوع/الشهر/السنة
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(today.getDate() - i);
        const dateKey = formatDate(date);
        dataMap[dateKey] = { time: dateKey, appOpen: 0, available: 0, orders: 0 };
      }
      
      activitiesData.forEach(activity => {
        const dateKey = activity.formattedDate;
        
        if (dataMap[dateKey]) {
          if (activity.type === 'app_open') dataMap[dateKey].appOpen += 1;
          if (activity.type === 'available_toggle' && activity.value === true) dataMap[dateKey].available += 1;
          if (activity.type === 'order_received') dataMap[dateKey].orders += 1;
        }
      });
    }

    setActivityChartData(Object.values(dataMap));
  }, [dateRange, formatDate]);

  // دالة لتجهيز بيانات مخطط الجاهزية
  const prepareAvailabilityData = useCallback((activitiesData) => {
    const availabilityByDriver = {};
    
    // تجميع نشاطات تغيير الجاهزية حسب السائق
    activitiesData.forEach(activity => {
      if (activity.type === 'available_toggle') {
        if (!availabilityByDriver[activity.driverName]) {
          availabilityByDriver[activity.driverName] = {
            name: activity.driverName,
            availableTime: 0,
            unavailableTime: 0
          };
        }
        
        // حساب وقت الجاهزية (تبسيط الحساب - يمكن تطويره لاحقًا)
        if (activity.value === true) {
          availabilityByDriver[activity.driverName].availableTime += 1;
        } else {
          availabilityByDriver[activity.driverName].unavailableTime += 1;
        }
      }
    });
    
    setAvailabilityData(Object.values(availabilityByDriver));
  }, []);

  // دالة لتجهيز بيانات السرعة
  const prepareSpeedData = useCallback((movementsData) => {
    const speedByDriver = {};
    
    // تجميع بيانات السرعة حسب السائق
    movementsData.forEach(movement => {
      if (movement.speed !== undefined) {
        if (!speedByDriver[movement.driverName]) {
          speedByDriver[movement.driverName] = {
            name: movement.driverName,
            averageSpeed: 0,
            maxSpeed: 0,
            totalReadings: 0,
            totalSpeed: 0
          };
        }
        
        // تحديث إحصائيات السرعة
        speedByDriver[movement.driverName].totalReadings += 1;
        speedByDriver[movement.driverName].totalSpeed += movement.speed;
        speedByDriver[movement.driverName].maxSpeed = Math.max(speedByDriver[movement.driverName].maxSpeed, movement.speed);
      }
    });
    
    // حساب متوسط السرعة لكل سائق
    Object.keys(speedByDriver).forEach(driverName => {
      const driver = speedByDriver[driverName];
      driver.averageSpeed = driver.totalReadings > 0 ? 
        (driver.totalSpeed / driver.totalReadings).toFixed(1) : 0;
    });
    
    setSpeedData(Object.values(speedByDriver));
  }, []);

  // دالة لحساب الإحصائيات العامة
  const calculateStats = useCallback((activitiesData, movementsData, driversData) => {
    // عدد السائقين الكلي
    const totalDrivers = driversData.length;
    
    // عدد السائقين النشطين (الذين فتحوا التطبيق خلال الفترة)
    const activeDriverIds = new Set();
    activitiesData.forEach(activity => {
      if (activity.type === 'app_open') {
        activeDriverIds.add(activity.driverId);
      }
    });
    const activeDrivers = activeDriverIds.size;
    
    // متوسط وقت النشاط (تبسيط - يمكن تطويره)
    const totalActiveMinutes = activitiesData.filter(a => a.type === 'app_open').length * 15; // افتراض 15 دقيقة لكل فتح تطبيق
    const averageActiveTime = activeDrivers > 0 ? totalActiveMinutes / activeDrivers : 0;
    
    // إجمالي وقت الجاهزية
    const totalAvailableTime = activitiesData.filter(a => a.type === 'available_toggle' && a.value === true).length * 60; // افتراض ساعة واحدة
    
    // متوسط السرعة
    let totalSpeed = 0;
    let speedReadings = 0;
    movementsData.forEach(movement => {
      if (movement.speed !== undefined) {
        totalSpeed += movement.speed;
        speedReadings += 1;
      }
    });
    const averageSpeed = speedReadings > 0 ? (totalSpeed / speedReadings).toFixed(1) : 0;
    
    setStats({
      totalDrivers,
      activeDrivers,
      averageActiveTime,
      averageSpeed,
      totalAvailableTime
    });
  }, []);

  // دالة لجلب بيانات السائقين ونشاطهم
const fetchDriverData = useCallback(async (isInitialLoad = true, currentLastVisible = lastVisible, currentPageSize = pageSize) => {
    console.log('fetchDriverData called, isInitialLoad:', isInitialLoad);
    try {
      if (isInitialLoad) {
        setLoading(true);
        setHasMore(true);
        setLastVisible(null);
        setAllDataFetched(false);
      } else {
        setLoadingMore(true);
      }
      
      // جلب قائمة السائقين
      const driversRef = collection(db, 'drivers');
      const driversSnapshot = await getDocs(driversRef);
      const driversData = driversSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        phone: doc.data().phone,
        ...doc.data()
      }));
      setDrivers(driversData);

      // حساب تاريخ البداية حسب النطاق المحدد
      const startDate = getStartDateFromRange(dateRange);
      
      // جلب نشاطات السائقين مع الصفحات
      const activitiesRef = collection(db, 'driverActivities');
let activitiesQuery = query(
  activitiesRef,
  where('timestamp', '>=', startDate),
  orderBy('timestamp', 'desc'),
limit(currentPageSize)
);

// إذا كانت صفحة تالية، نبدأ بعد آخر عنصر تم جلبه
if (!isInitialLoad && lastVisible) {
  activitiesQuery = query(
    activitiesRef,
    where('timestamp', '>=', startDate),
    orderBy('timestamp', 'desc'),
    startAfter(lastVisible),
    limit(pageSize)
  );
}

const activitiesSnapshot = await getDocs(activitiesQuery);
let activitiesData = activitiesSnapshot.docs.map(doc => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    date: data.timestamp?.toDate() || new Date(),
    formattedDate: data.timestamp?.toDate() ? formatDate(data.timestamp.toDate()) : formatDate(new Date()),
    formattedTime: data.timestamp?.toDate() ? formatTime(data.timestamp.toDate()) : formatTime(new Date())
  };
});

// ✅ فلترة حسب السائق إذا لم يكن "all"
if (selectedDriver !== 'all') {
  activitiesData = activitiesData.filter(a => a.driverId === selectedDriver);
}

// تحديث حالة آخر عنصر مرئي للصفحات التالية
if (activitiesSnapshot.docs.length > 0) {
  setLastVisible(activitiesSnapshot.docs[activitiesSnapshot.docs.length - 1]);
  setHasMore(activitiesSnapshot.docs.length >= pageSize);
} else {
  setHasMore(false);
  setAllDataFetched(true);
}

// تحديث حالة نشاطات السائقين
if (isInitialLoad) {
  setDriverActivities(activitiesData);
} else {
  setDriverActivities(prevActivities => [...prevActivities, ...activitiesData]);
}


      // جلب سجل تحركات السائقين مع التصفح
      const movementsRef = collection(db, 'driverMovements');
let movementsQuery = query(
  movementsRef,
  where('timestamp', '>=', startDate),
  orderBy('timestamp', 'desc'),
  limit(pageSize)
);

// إذا كانت صفحة تالية، نبدأ بعد آخر عنصر تم جلبه
if (!isInitialLoad && lastVisible) {
  movementsQuery = query(
    movementsRef,
    where('timestamp', '>=', startDate),
    orderBy('timestamp', 'desc'),
    startAfter(lastVisible),
    limit(pageSize)
  );
}

const movementsSnapshot = await getDocs(movementsQuery);
let movementsData = movementsSnapshot.docs.map(doc => {
  const data = doc.data();
  return {
    id: doc.id,
    ...data,
    date: data.timestamp?.toDate() || new Date(),
    formattedDate: data.timestamp?.toDate() ? formatDate(data.timestamp.toDate()) : formatDate(new Date()),
    formattedTime: data.timestamp?.toDate() ? formatTime(data.timestamp.toDate()) : formatTime(new Date())
  };
});

// ✅ فلترة حسب السائق إذا لم يكن "all"
if (selectedDriver !== 'all') {
  movementsData = movementsData.filter(m => m.driverId === selectedDriver);
}

setDriverMovements(movementsData);


      // تحضير بيانات الرسوم البيانية - نستخدم البيانات المحدودة للعرض البياني
      prepareActivityChartData(activitiesData);
      prepareAvailabilityData(activitiesData);
      prepareSpeedData(movementsData);
      calculateStats(activitiesData, movementsData, driversData);

    } catch (error) {
      console.error('خطأ في جلب بيانات السائقين:', error);
      setError('حدث خطأ في تحميل البيانات. يرجى التأكد من الاتصال بقاعدة البيانات أو مراجعة الصلاحيات.');
      alert('حدث خطأ في تحميل البيانات. يرجى تحديث الصفحة والمحاولة مرة أخرى.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      console.log('fetchDriverData finished');
    }
}, [dateRange, selectedDriver, formatDate, formatTime, prepareActivityChartData, prepareAvailabilityData, prepareSpeedData, calculateStats, pageSize]);

  // دالة لتحميل المزيد من البيانات عند التمرير
  const loadMoreData = useCallback(() => {
    if (!loadingMore && hasMore && !allDataFetched) {
      fetchDriverData(false);
    }
  }, [loadingMore, hasMore, fetchDriverData, allDataFetched]);

  // تحويل نطاق التاريخ إلى تاريخ البداية
  const getStartDateFromRange = (range) => {
    const now = new Date();
    const startDate = new Date();
    
    switch(range) {
      case 'day':
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(now.getMonth() - 1);
        break;
      case 'year':
        startDate.setFullYear(now.getFullYear() - 1);
        break;
      default:
        startDate.setDate(now.getDate() - 7);
        break;
    }
    
    return startDate;
  };

  // التحقق من المستخدم عند التحميل
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        setLoading(false);
        return;
      }
      
      // التحقق من صلاحيات المستخدم (يمكن تعديله حسب هيكل التطبيق)
      const userRole = localStorage.getItem('userRole');
      if (userRole !== 'admin') {
        navigate('/');
        setLoading(false);
        return;
      }
      
      try {
        await fetchDriverData();
      } catch (error) {
        console.error('خطأ في جلب البيانات الأولية:', error);
        setLoading(false); // تأكد من إيقاف حالة التحميل عند حدوث خطأ
        alert('حدث خطأ في تحميل البيانات. يرجى تحديث الصفحة والمحاولة مرة أخرى.');
      }
    });
    
    return () => unsubscribe();
  }, [navigate, fetchDriverData]);

  // استخراج التقرير بصيغة Excel
   const exportToExcel = () => {
    let data = [], fileName = 'تقرير_تتبع_السائقين';

    if (reportType === 'activity') {
      fileName = 'تقرير_نشاط_السائقين';
      data = driverActivities.map(a => ({
        'التاريخ': a.formattedDate,
        'الوقت':    a.formattedTime,
        'اسم السائق': a.driverName,
        'نوع النشاط': getActivityTypeName(a.type),
        'القيمة':   typeof a.value==='boolean'? (a.value?'نعم':'لا') : a.value || '-'
      }));
    }
    if (reportType === 'movements') {
      fileName = 'تقرير_تحركات_السائقين';
      data = driverMovements.map(m => ({
        'التاريخ': m.formattedDate,
        'الوقت':    m.formattedTime,
        'اسم السائق': m.driverName,
        'خط العرض': m.location?.lat || '-',
        'خط الطول': m.location?.lng || '-',
        'السرعة (كم/س)': m.speed || '0',
        'الوجهة':  m.destination || '-'
      }));
    }
    if (reportType === 'availability') {
      fileName = 'تقرير_جاهزية_السائقين';
      data = driverActivities
        .filter(a=>a.type==='available_toggle')
        .map(a=>({
          'التاريخ': a.formattedDate,
          'الوقت':    a.formattedTime,
          'اسم السائق': a.driverName,
          'الحالة':  a.value?'متاح':'غير متاح',
          'ملاحظات': a.notes||'-'
        }));
    }

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');
    XLSX.writeFile(wb, `${fileName}.xlsx`);
  };
  
  // استخراج التقرير بصيغة PDF
 const exportToPDF = () => {
   const doc = new jsPDF({ orientation: 'landscape', format: 'a4' });
addArabicFont(doc);              // تحميل وتسجيل خط Amiri
doc.setFont('Amiri', 'normal');  // ضبط الخط الفعلي
doc.setFontSize(14);
doc.setTextColor(40);

    // Title & headers/rows
   let title = /* حسب reportType */ 'تقرير نشاط السائقين';
      doc.text(title, 150, 20, { align: 'center' });
    let headers = [], rows = [];

    if (reportType === 'activity') {
      title = 'تقرير نشاط السائقين';
      headers = ['التاريخ','الوقت','اسم السائق','نوع النشاط','القيمة'];
      rows = driverActivities.map(a => [
        a.formattedDate,
        a.formattedTime,
        a.driverName,
        getActivityTypeName(a.type),
        typeof a.value==='boolean'? (a.value?'نعم':'لا') : a.value || '-'
      ]);
    }
    if (reportType === 'movements') {
      title = 'تقرير تحركات السائقين';
      headers = ['التاريخ','الوقت','اسم السائق','خط العرض','خط الطول','السرعة (كم/س)','الوجهة'];
      rows = driverMovements.map(m => [
        m.formattedDate,
        m.formattedTime,
        m.driverName,
        m.location?.lat || '-',
        m.location?.lng || '-',
        m.speed || '0',
        m.destination || '-'
      ]);
    }
    if (reportType === 'availability') {
      title = 'تقرير جاهزية السائقين';
      headers = ['التاريخ','الوقت','اسم السائق','الحالة','ملاحظات'];
      rows = driverActivities
        .filter(a=>a.type==='available_toggle')
        .map(a=>[
          a.formattedDate,
          a.formattedTime,
          a.driverName,
          a.value?'متاح':'غير متاح',
          a.notes||'-'
        ]);
    }

    // draw title
    doc.text(title, 150, 20, { align: 'center' });

    // رسم الجدول
   autoTable(doc, {
     head: [headers],
     body: rows,
     styles: {
       font: 'Amiri',
       fontStyle: 'normal',
       fontSize: 10,
       halign: 'right',
       cellPadding: 3
     },
     headStyles: {
       fillColor: [40, 60, 90],
       textColor: 255,
       fontSize: 12,
       halign: 'right'
     },
     margin: { top: 30 },
     theme: 'grid'
   });

    // add page info (footer)
    // save
    doc.save(`${title.replace(/ /g,'_')}.pdf`);
  };

  // تحويل نوع النشاط إلى اسم مقروء
  const getActivityTypeName = (type) => {
    switch (type) {
      case 'app_open': return t('activity_app_open');
      case 'app_close': return t('activity_app_close');
      case 'available_toggle': return t('activity_available_toggle');
      case 'order_received': return t('activity_order_received');
      case 'order_completed': return t('activity_order_completed');
      case 'login': return t('activity_login');
      case 'logout': return t('activity_logout');
      case 'navigate_to_restaurant': return t('activity_navigate_to_restaurant');
      case 'navigate_to_customer': return t('activity_navigate_to_customer');
      case 'order_status_change': return t('activity_order_status_change');
      default: return type;
    }
  };

  return (
    <div className="driver-tracking-reports-layout">
      <AdminSidebar />
      <div className="driver-tracking-reports-content">
        {error ? (
          <div style={{color:'red',textAlign:'center',marginTop:'40px',fontSize:'20px'}}>{error}</div>
        ) : loading ? (
          <LoadingSpinner text={t('loading')} />
        ) : (
          <>
            <div className="reports-header">
              <h1>{t('driver_tracking_reports')}</h1>
              <div className="reports-controls">
                <div className="export-buttons">
                  <button onClick={exportToExcel} className="export-btn">
                    <span className="export-icon">📥</span>
                    <span>{t('export_excel')}</span>
                  </button>
                  <button onClick={exportToPDF} className="export-btn">
                    <span className="export-icon">📄</span>
                    <span>{t('export_pdf')}</span>
                  </button>
                </div>
                <div className="filters-section">
                  <div className="filter-group">
                    <label>{t('report_type')}:</label>
                    <select 
                      value={reportType} 
                      onChange={(e) => setReportType(e.target.value)}
                    >
                      <option value="activity">{t('activity_report')}</option>
                      <option value="movements">{t('movements_report')}</option>
                      <option value="availability">{t('availability_report')}</option>
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>{t('date_range')}:</label>
                    <select 
                      value={dateRange} 
                      onChange={(e) => setDateRange(e.target.value)}
                    >
                      <option value="day">{t('today')}</option>
                      <option value="week">{t('last_week')}</option>
                      <option value="month">{t('last_month')}</option>
                      <option value="year">{t('last_year')}</option>
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>{t('driver')}:</label>
                    <select 
                      value={selectedDriver} 
                      onChange={(e) => setSelectedDriver(e.target.value)}
                    >
                      <option value="all">{t('all_drivers')}</option>
                      {drivers.map(driver => (
                        <option key={driver.id} value={driver.id}>{driver.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="filter-group">
                    <label>{t('page_size')}:</label>
                    <select 
                      value={pageSize} 
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setLastVisible(null);
                        setAllDataFetched(false);
                        setHasMore(true);
                      }}
                    >
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div className="stats-cards">
              <div className="stat-card">
                <div className="stat-value">{stats.totalDrivers}</div>
                <div className="stat-label">{t('total_drivers')}</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-value">{stats.activeDrivers}</div>
                <div className="stat-label">{t('active_drivers')}</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-value">{Math.round(stats.averageActiveTime)} {t('minutes')}</div>
                <div className="stat-label">{t('average_active_time')}</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-value">{stats.averageSpeed} {t('km_per_hour')}</div>
                <div className="stat-label">{t('average_speed')}</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-value">{Math.round(stats.totalAvailableTime / 60)} {t('hours')}</div>
                <div className="stat-label">{t('total_available_time')}</div>
              </div>
            </div>
            <div className="reports-charts">
              {reportType === 'activity' && (
                <div className="chart-container">
                  <h2>{t('activity_report')}</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={activityChartData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="time" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="appOpen" name={t('app_open')} fill="#0088FE" />
                      <Bar dataKey="available" name={t('available')} fill="#00C49F" />
                      <Bar dataKey="orders" name={t('orders')} fill="#FFBB28" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              
              {reportType === 'availability' && (
                <div className="chart-container">
                  <h2>{t('availability_report')}</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={availabilityData} layout="vertical" margin={{ top: 20, right: 30, left: 100, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="availableTime" name={t('available_time')} fill="#00C49F" />
                      <Bar dataKey="unavailableTime" name={t('unavailable_time')} fill="#FF8042" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              
              {reportType === 'movements' && (
                <div className="chart-container">
                  <h2>{t('movements_report')}</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={speedData} margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="averageSpeed" name={t('average_speed')} fill="#0088FE" />
                      <Bar dataKey="maxSpeed" name={t('max_speed')} fill="#FF8042" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
            <div className="reports-data-table">
              <h2>
                {reportType === 'activity' && t('activity_details')}
                {reportType === 'movements' && t('movements_details')}
                {reportType === 'availability' && t('availability_details')}
              </h2>
              
              <div className="table-container">
                {reportType === 'activity' && (
                  <>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('date')}</th>
                          <th>{t('time')}</th>
                          <th>{t('driver')}</th>
                          <th>{t('activity_type')}</th>
                          <th>{t('value')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverActivities.length === 0 && !loadingMore ? (
                          <tr>
                            <td colSpan="5" className="no-data">{t('no_data_for_period_or_filter')}</td>
                          </tr>
                        ) : (
                          driverActivities.map(activity => (
                            <tr key={activity.id}>
                              <td>{activity.formattedDate}</td>
                              <td>{activity.formattedTime}</td>
                              <td>{activity.driverName}</td>
                              <td>{getActivityTypeName(activity.type)}</td>
                              <td>
                                {typeof activity.value === 'boolean' 
                                  ? (activity.value ? t('yes') : t('no')) 
                                  : (activity.value || '-')}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    
                    {loadingMore && (
                      <div className="loading-more">
                        <div className="spinner-small"></div>
                        <p>{t('loading_more_data')}</p>
                      </div>
                    )}
                    
                    {!loadingMore && hasMore && !allDataFetched && (
                      <div className="load-more-container">
                        <button onClick={loadMoreData} className="load-more-btn">
                          {t('load_more_data')}
                        </button>
                      </div>
                    )}
                    
                    {!hasMore && driverActivities.length > 0 && (
                      <div className="end-of-data">{t('all_data_displayed')}</div>
                    )}
                  </>
                )}
                
                {reportType === 'movements' && (
                  <>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('date')}</th>
                          <th>{t('time')}</th>
                          <th>{t('driver')}</th>
                          <th>{t('location')}</th>
                          <th>{t('speed')}</th>
                          <th>{t('destination')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverMovements.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="no-data">{t('no_data_for_period')}</td>
                          </tr>
                        ) : (
                          driverMovements.map(movement => (
                            <tr key={movement.id}>
                              <td>{movement.formattedDate}</td>
                              <td>{movement.formattedTime}</td>
                              <td>{movement.driverName}</td>
                              <td>
                                {movement.location ? 
                                  `${movement.location.lat.toFixed(6)}, ${movement.location.lng.toFixed(6)}` : 
                                  t('not_available')}
                              </td>
                              <td>{movement.speed || '0'}</td>
                              <td>{movement.destination || '-'}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    
                    {loadingMore && (
                      <div className="loading-more">
                        <div className="spinner-small"></div>
                        <p>{t('loading_more_data')}</p>
                      </div>
                    )}
                    
                    {!loadingMore && hasMore && driverMovements.length > 0 && (
                      <div className="load-more-container">
                        <button onClick={loadMoreData} className="load-more-btn">
                          {t('load_more_data')}
                        </button>
                      </div>
                    )}
                    
                    {!hasMore && driverMovements.length > 0 && (
                      <div className="end-of-data">{t('all_data_displayed')}</div>
                    )}
                  </>
                )}
                
                {reportType === 'availability' && (
                  <>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>{t('date')}</th>
                          <th>{t('time')}</th>
                          <th>{t('driver')}</th>
                          <th>{t('status')}</th>
                          <th>{t('notes')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {driverActivities.filter(activity => activity.type === 'available_toggle').length === 0 ? (
                          <tr>
                            <td colSpan="5" className="no-data">{t('no_data_for_period')}</td>
                          </tr>
                        ) : (
                          driverActivities
                            .filter(activity => activity.type === 'available_toggle')
                            .map(activity => (
                              <tr key={activity.id}>
                                <td>{activity.formattedDate}</td>
                                <td>{activity.formattedTime}</td>
                                <td>{activity.driverName}</td>
                                <td>
                                  <span className={`status-badge ${activity.value ? 'available' : 'unavailable'}`}>
                                    {activity.value ? t('available') : t('unavailable')}
                                  </span>
                                </td>
                                <td>{activity.notes || '-'}</td>
                              </tr>
                            ))
                        )}
                      </tbody>
                    </table>
                    
                    {loadingMore && (
                      <div className="loading-more">
                        <div className="spinner-small"></div>
                        <p>{t('loading_more_data')}</p>
                      </div>
                    )}
                    
                    {!loadingMore && hasMore && driverActivities.filter(activity => activity.type === 'available_toggle').length > 0 && (
                      <div className="load-more-container">
                        <button onClick={loadMoreData} className="load-more-btn">
                          {t('load_more_data')}
                        </button>
                      </div>
                    )}
                    
                    {!hasMore && driverActivities.filter(activity => activity.type === 'available_toggle').length > 0 && (
                      <div className="end-of-data">{t('all_data_displayed')}</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
  }; 
export default DriverTrackingReports;